import {useDispatch, useSelector} from "react-redux";
import {useEffect, useRef, useState} from "react";
import {
    Alert,
    Avatar,
    Button,
    Grid,
    Input,
    ProgressBar,
    Row,
    styles,
    Surface,
    Switch,
    Text
} from "../../tmwx-design-web/src";
import {useTranslation} from "react-i18next";
import {checkFieldError, isEmpty} from "../../gui-common/appHelper";
import {RootState} from "../../gui-common/redux/store";
import {ResponseBase} from "../../gui-common/redux/data";
import {setSnackbarData} from "../../gui-common/redux/action/snackbarAction";
import {useVirtualizer} from "@tanstack/react-virtual";
import {addNewPod, clearPodError, clearPodResponse, getPodList} from "gui-common/redux/action/efe/podAction";
import {Company} from "../../gui-common/redux/data/company";
import {getFieldErrorText} from "../../utils/helper";
import {usePermissions} from "tmwx-design-web/src/tmwx-design-common";
import {Location} from "gui-common/redux/data/location";
import {Device} from "gui-common/redux/data/device";
import {AddPodRequest, initialAddPodRequest, isAddPodRequestValid, Pod} from "gui-common/redux/data/pod";

interface HandleAddMeasurementLocationProps {
    actionType: 'edit' | 'add'
    item?: Pod | null
    onClose?: () => void
}

const namePattern = /[^a-zA-ZáéíóöőúüűÁÉÍÓÖŐÚÜŰ0-9 ]/g;

const HandleAddMeasurementLocation: React.FC<HandleAddMeasurementLocationProps> = ({actionType, item, onClose = () => {}}) => {
    const {t} = useTranslation()
    const dispatch: any = useDispatch();
    const {hasRights} = usePermissions()
    const serverError = useSelector<RootState, ResponseBase | null>(state => state.pod.error);
    const isLoading = useSelector<RootState, boolean>(state => state.pod.loading);
    const response = useSelector<RootState, ResponseBase | null>(state => state.pod.response);
    const deviceList = useSelector<RootState, Device[]>(state => state.device.deviceList);
    const locationList = useSelector<RootState, Location[]>(state => state.atrLocation.locationList);
    const activeCompany = useSelector<RootState, Company>(state => state.auth.activeCompany);
    const [validationMessage, setValidationMessage] = useState<string>("");
    const [request, setRequest] = useState<AddPodRequest>(initialAddPodRequest);
    const deviceListRef = useRef<any>()
    const locationListRef = useRef<any>()
    const [locationFilter, setLocationFilter] = useState<string>('')
    const [deviceFilter, setDeviceFilter] = useState<string>('')
    const [deviceListHeight, setDeviceListHeight] = useState< 200 | 300 | 500 | 650>(200)
    const [locationListHeight, setLocationListHeight] = useState< 200 | 350 | 500 | 650>(200)
    const hasPodCreateOrEditRight = hasRights(['efe_efe_list']) && !hasRights(['efe_location_list'])

    const virtualizerDevice = useVirtualizer({
        count: isEmpty(deviceFilter) ? deviceList.length : deviceList.length,
        getScrollElement: () => deviceListRef.current,
        estimateSize: () => 44,
        overscan: 30
    })

    const virtualizerLocation = useVirtualizer({
        count: isEmpty(locationFilter) ? locationList.length : locationList.length,
        getScrollElement: () => locationListRef.current,
        estimateSize: () => 44,
        overscan: 30
    })

    const virtualizedDeviceList = virtualizerDevice.getVirtualItems()
    const virtualizedLocationList = virtualizerLocation.getVirtualItems()

    const handleClear = () => {
        setRequest(initialAddPodRequest);
        dispatch(clearPodResponse())
        dispatch(clearPodError())
        onClose()
    }

    const handleNameChange = (text: string) => {
        const hasSpecialChars = namePattern.test(text);
        if (hasSpecialChars) {
            setValidationMessage(t('nameSpecialChars'));
        } else {
            setValidationMessage("");
            setRequest({
                ...request,
                CustomerName: text
            });
        }
    }

    const handlePodIdChange = (text: string) => {
        const hasSpecialChars = namePattern.test(text);
        if (hasSpecialChars) {
            setValidationMessage(t('nameSpecialChars'));
        } else {
            setValidationMessage("");
            setRequest({
                ...request,
                podId: text
            });
        }
    }

    const handlePodClick = () => {
        if (item && actionType === 'edit') {
            ///modify
            setValidationMessage('')

            // let editRequest: EditUserRequest = {
            //     id: item.id,
            //     name: request.name,
            //     username: request.username,
            //     email: request.email,
            //     roles: request.roles,
            //     companyId: request.companyId ? request.companyId : null,
            //     groups: []
            // }
            //
            // if (isEditATRUserRequestValid(editRequest) || request.companyId !== null) {
            //     dispatch(editATRUser(editRequest));
            // } else {
            //     setValidationMessage(t('mandatoryFields'));
            // }
        } else {
            //save
            setValidationMessage('')

            setRequest({
                ...request,
                companyId: activeCompany.id
            });
            if (isAddPodRequestValid(request as AddPodRequest)) {
                dispatch(addNewPod(request as AddPodRequest));
            } else {
                setValidationMessage(t('mandatoryFields'));
            }
        }
    }

    const handleSelectLocation = (id: number) => {
        setRequest({...request, locationId: id})
    }

    const handleSelectSystemId = (systemId: string) => {
        setRequest({...request, systemId: systemId})
    }

    /* HANDLE EDIT REQUEST VALUES */
    useEffect(() => {
        if (item) {

            let obj: AddPodRequest = {
                CustomerName: item.customerName ? item.customerName : '',
                systemId: item.systemId ? item.systemId : '',
                podId: item.podId ? item.podId : '',
                companyId: activeCompany.id,
                locationId: -1
            }

            setRequest(obj)
        }
    }, [item])

    /* HANDLE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                handleClear()
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
                dispatch(getPodList(activeCompany.id));
            } else {
                dispatch(setSnackbarData({
                    status: 'danger',
                    text: t('actionFailed')
                }))
            }
        }
    }, [response])


    return (
        <Surface elevation={0}>
            {isLoading && <ProgressBar animated rounded={false} width={'100%'} height={6} vertical={false}/>}

            <Grid.Container centered maxWidth="xl">
                <Grid.Col m={hasPodCreateOrEditRight ? 12 : 6} lg={hasPodCreateOrEditRight ? 12 : 6}
                          xl={hasPodCreateOrEditRight ? 12 : 6}>
                    <Grid.Container>
                        <Grid.Col style={{padding: 0}}>
                            <Text bold>
                                {t('data')}
                            </Text>

                            {serverError && (
                                <Alert style={styles.marginTop8} status="danger" text={t('errorOccurred')}/>
                            )}

                            {!isEmpty(validationMessage) && (
                                <Alert style={styles.marginTop8} text={validationMessage} status="danger"/>
                            )}

                            <Input
                                style={styles.marginTop8}
                                fullWidth
                                error={checkFieldError('name', serverError)}
                                icon={"badge_outlined"}
                                placeholder={t('name')}
                                value={request.CustomerName}
                                onChangeValue={(text) => handleNameChange(text)}
                                helperText={getFieldErrorText('name', serverError, t)}
                            />

                            <Input
                                style={styles.marginTop8}
                                fullWidth
                                error={checkFieldError('registrationId', serverError)}
                                icon={"person_outlined"}
                                placeholder={t('efe.measurementLocId')}
                                value={request.podId}
                                onChangeValue={(text) => handlePodIdChange(text)}
                                helperText={getFieldErrorText('registrationId', serverError, t)}
                            />

                        </Grid.Col>

                        <Grid.Col style={{padding: 0}}>
                            <Text bold style={styles.marginTop16}>
                                {t('efe.id')}
                            </Text>

                            <Surface style={{...styles.marginTop4, ...{padding: 4, paddingBottom: 0}}}>
                                <div>
                                    <Input
                                        style={styles.marginTop8}
                                        fullWidth
                                        icon={"search_outlined"}
                                        placeholder={t('search')}
                                        value={deviceFilter}
                                        onChangeValue={(text) => setDeviceFilter(text)}
                                    />
                                    <div ref={deviceListRef}
                                         style={{...styles.width100p, ...{height: deviceListHeight, overflow: 'auto'}}}>
                                        <div
                                            style={{
                                                height: `${virtualizerDevice.getTotalSize()}px`,
                                                width: '100%',
                                                position: 'relative'
                                            }}
                                        >
                                            {virtualizedDeviceList.map((virtualItem) => (
                                                <Row
                                                    style={{
                                                        /* filtering data with styles */
                                                        display: !isEmpty(deviceFilter) ? (deviceList[virtualItem.index].systemId.toLowerCase().includes(deviceFilter.toLocaleLowerCase()) ? 'block' : 'none') : 'block'
                                                    }}
                                                    key={virtualItem.key}
                                                    title={deviceList[virtualItem.index].systemId}
                                                    avatar={<Avatar size='small' iconName={'work_outlined'}
                                                                    color={request.systemId === deviceList[virtualItem.index].systemId ? 'primary' : 'neutral'}/>}
                                                    actionRight={<Switch
                                                        isEnabled={request.systemId === deviceList[virtualItem.index].systemId}
                                                        toggleSwitch={() => handleSelectSystemId(deviceList[virtualItem.index].systemId)}/>}
                                                    selected={request.systemId === deviceList[virtualItem.index].systemId}
                                                    touchable
                                                    touchableAction={() => handleSelectSystemId(deviceList[virtualItem.index].systemId)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </Surface>
                        </Grid.Col>

                    </Grid.Container>
                </Grid.Col>

                {hasPodCreateOrEditRight ? null : (
                    <Grid.Col m={6} lg={6} xl={6}>
                        <Text bold>
                            {t('atr.machines.location')}
                        </Text>

                        <Surface style={{...styles.marginTop8, ...{padding: 4, paddingBottom: 0}}}>
                            <div>
                                <Input
                                    style={styles.marginTop8}
                                    fullWidth
                                    icon={"search_outlined"}
                                    placeholder={t('search')}
                                    value={locationFilter}
                                    onChangeValue={(text) => setLocationFilter(text)}
                                />
                                <div ref={locationListRef}
                                     style={{...styles.width100p, ...{height: locationListHeight, overflow: 'auto'}}}>
                                    <div
                                        style={{
                                            height: `${virtualizerLocation.getTotalSize()}px`,
                                            width: '100%',
                                            position: 'relative'
                                        }}
                                    >
                                        {virtualizedLocationList.map((virtualItem) => (
                                            <Row
                                                style={{
                                                    /* filtering data with styles */
                                                    display: !isEmpty(locationFilter) ? (locationList[virtualItem.index].address.toLowerCase().includes(locationFilter.toLocaleLowerCase()) ? 'block' : 'none') : 'block'
                                                }}
                                                key={virtualItem.key}
                                                title={locationList[virtualItem.index].address}
                                                avatar={<Avatar size='small' iconName={'work_outlined'}
                                                                color={request.locationId === locationList[virtualItem.index].locationId ? 'primary' : 'neutral'}/>}
                                                actionRight={<Switch
                                                    isEnabled={request.locationId === locationList[virtualItem.index].locationId}
                                                    toggleSwitch={() => handleSelectLocation(locationList[virtualItem.index].locationId)}/>}
                                                selected={request.locationId === locationList[virtualItem.index].locationId}
                                                touchable
                                                touchableAction={() => handleSelectLocation(locationList[virtualItem.index].locationId)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Surface>
                    </Grid.Col>
                )}
            </Grid.Container>

            <div style={{...styles.marginTop8, ...styles.row, ...{justifyContent: 'flex-end'}}}>
                <Button status="danger" variant="outlined" onPress={handleClear}>
                    {t('cancel')}
                </Button>
                <Button style={styles.marginLeft16} onPress={handlePodClick}>
                    {actionType === 'add' ? t('add') : t('modify')}
                </Button>
            </div>
        </Surface>
    )
};

export default HandleAddMeasurementLocation;
